import {AfterViewInit, Component, DOCUMENT, Inject, OnInit, Renderer2, signal, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {Router, RouterOutlet} from '@angular/router';
import {TranslocoModule, TranslocoService} from '@jsverse/transloco';
import {MessageService} from 'primeng/api';
import {LayoutModule} from './core/modules/layout-module';
import {PrimeModule} from './core/modules/prime-module';
import {environment} from '../environments/environment';
import {AppGlobalEventManagerService} from './core/services/app.globalEventManager.service';
import {AppSessionService} from './core/services/app.session.service';
import {AppAuthService} from './core/services/app.auth.service';
import {LoopBackConfig} from './shared/sdk';
import {ROLES} from './core/datas/roles.helpers';
import {PrimeNG} from 'primeng/config';
import {PrimeNGTranslationService} from './core/stores/primeng-translation.service';
import {AppToasterService} from './core/services/app.toaster.service';
import {NgProgressbar} from 'ngx-progressbar';
import {NgProgressHttp} from 'ngx-progressbar/http';
import {NgxSpinnerComponent, NgxSpinnerModule, NgxSpinnerService} from 'ngx-spinner';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import {TenantService} from './core/services/tenant.service';
import {TenantBrandingPalette} from './core/interfaces/tenant.config.interface';
import { updatePreset } from '@primeng/themes';
import { buildTenantPresetOverride } from './core/datas/tenant.preset';

// Titre d'onglet par défaut (identité VGE) : valeur historique de
// `index.html` (<title>VGE - eGestion</title>). Sans `branding.titre` dans la
// config tenant, ce défaut est posé → le titre VGE reste strictement inchangé.
export const TITRE_ONGLET_DEFAUT_VGE = 'VGE - eGestion';

// Résout le titre d'onglet à partir du branding tenant : `branding.titre`
// s'il est défini, sinon le défaut VGE. Fonction pure et exportée pour être
// couverte par une spec sans bootstrapper tout le composant racine.
export function resolveTitreOnglet(branding: { titre?: string } | null | undefined): string {
  return (branding && branding.titre) || TITRE_ONGLET_DEFAUT_VGE;
}

// Branding couleur par tenant (D6 complet). Mappe la palette tenant vers les
// variables CSS de marque à poser sur le `:root` — miroir EXACT des `:root` de
// `assets/themes/VGE/VGE.scss`. Ces 7 nuances pilotent toute l'identité colorée
// (boutons `btn-vge-*` et utilitaires `bg|border|ring|text-vge-*`, item de menu
// actif) ; `--color-VGE` / `--primary-color` sont les alias historiques de
// `lower`.
//
// INVARIANT VGE : palette absente (cas VGE — `environment.ts` ne déclare aucune
// palette) → tableau VIDE → aucune surcharge posée → les valeurs de VGE.scss
// restent en vigueur → rendu VGE identique au pixel. Fonction pure et exportée
// pour être couverte par une spec sans DOM ni bootstrap du composant racine.
export function paletteToCssVars(
  palette: TenantBrandingPalette | null | undefined
): Array<[string, string]> {
  if (!palette) {
    return [];
  }
  return [
    ['--color-brand-lowest', palette.lowest],
    ['--color-brand-lower', palette.lower],
    ['--color-brand-low', palette.low],
    ['--color-brand-medium', palette.medium],
    ['--color-brand-hight', palette.hight],
    ['--color-brand-highter', palette.highter],
    ['--color-brand-hightest', palette.hightest],
    // Alias historiques pointant sur `lower` dans VGE.scss.
    ['--color-VGE', palette.lower],
    ['--primary-color', palette.lower],

    // Utilitaires Tailwind `teal-*` / `emerald-*` : les verts de marque VGE sont
    // codés en CLASSES dans les templates (bg-teal-50, text-teal-700, gradients
    // emerald…). Tailwind 4 résout ces classes via var(--color-teal-*) : on
    // re-teinte donc les deux gammes vers la palette du tenant ICI, sans toucher
    // aux templates. VGE : palette absente → [] → gammes Tailwind d'origine.
    ...(['teal', 'emerald'] as const).flatMap((gamme): Array<[string, string]> => [
      [`--color-${gamme}-50`,  palette.lower],
      [`--color-${gamme}-100`, palette.lower],
      [`--color-${gamme}-200`, palette.low],
      [`--color-${gamme}-300`, palette.low],
      [`--color-${gamme}-400`, palette.low],
      [`--color-${gamme}-500`, palette.medium],
      [`--color-${gamme}-600`, palette.medium],
      [`--color-${gamme}-700`, palette.hight],
      [`--color-${gamme}-800`, palette.highter],
      [`--color-${gamme}-900`, palette.hightest],
      [`--color-${gamme}-950`, palette.hightest],
    ]),
  ];
}

@Component({
  selector: 'app-VGE',
  imports: [TranslocoModule, LayoutModule, PrimeModule, RouterOutlet, NgProgressbar, NgProgressHttp, NgxSpinnerComponent, NgxSpinnerModule, NgxExtendedPdfViewerModule],
  standalone: true,
  providers: [],
  templateUrl: './app.html'
})
export class App implements OnInit, AfterViewInit {

  public showChat = false;
  public showChatPublic = false;
  public padding: number = 0;

  constructor(
    private router: Router,
    private primeng: PrimeNG,
    private translocoService: TranslocoService,
    private primeNGTranslationService: PrimeNGTranslationService,
    private _appGlobalEventManagerService: AppGlobalEventManagerService,
    private _sessionService: AppSessionService,
    private _authService: AppAuthService,
    public renderer2: Renderer2,
    private spinner: NgxSpinnerService,
    private title: Title,
    private tenantService: TenantService,
    @Inject(DOCUMENT) document: any
  ) {

    LoopBackConfig.setBaseURL(environment.urlnodeJS);
    LoopBackConfig.setApiVersion(environment.ApinodeJS);

    // Titre d'onglet par tenant (branding minimal). Défaut = titre historique
    // VGE : l'identité VGE est préservée tant qu'aucun `branding.titre` n'est
    // configuré. Posé au plus tôt (constructeur) pour éviter tout flash.
    this.title.setTitle(resolveTitreOnglet(this.tenantService.branding));

    // Palette couleur par tenant (branding D6 complet). On pose les variables
    // CSS de marque sur le `:root` (document.documentElement), à côté du titre
    // et au plus tôt pour éviter tout flash. VGE ne déclare AUCUNE palette →
    // `paletteToCssVars` renvoie [] → aucune propriété posée → les défauts de
    // VGE.scss règnent → rendu VGE inchangé au pixel.
    for (const [prop, value] of paletteToCssVars(this.tenantService.branding.palette)) {
      document.documentElement.style.setProperty(prop, value);
    }

    // Fond de page teinté par tenant (valeurs client 2026-07-30). Style inline
    // sur <body> → prime sur le `bg-gray-50` du thème tailAdmin. VGE ne déclare
    // AUCUN `bodyBackground` → aucun style posé → rendu VGE inchangé.
    const bodyBackground = this.tenantService.branding.bodyBackground;
    if (bodyBackground) {
      document.body.style.backgroundColor = bodyBackground;
    }

    // Preset PrimeNG dynamique par tenant (volet 2). SI et SEULEMENT SI une
    // palette tenant est déclarée (⇒ tenant ≠ vge), on surcharge le preset Aura
    // custom (primary 50→950 + colorScheme light/dark) avec les nuances de marque
    // du tenant → tous les composants PrimeNG bruts (select, checkbox, highlight
    // de table, focus, tags/messages primaires…) adoptent la couleur du tenant.
    // updatePreset FUSIONNE (merge) : les components.* du preset statique sont
    // conservés. VGE ne déclare AUCUNE palette → aucun appel → le preset statique
    // teal (custom.preset.ts) reste en vigueur, byte-identique.
    const brandPalette = this.tenantService.branding.palette;
    if (brandPalette) {
      updatePreset(buildTenantPresetOverride(brandPalette));
    }

    // Charger les paramètres une seule fois au démarrage
    this._appGlobalEventManagerService.getApiParameters().subscribe(() => {
      /* Check current Login */
      if (this._authService.getAccessTokenId()) {
        this._sessionService.updateLogguedAccount(this._authService.getCurrentUserData());
      } else {
        this.router.navigate(['/login']);
      }

      this._sessionService.logguedAccountEmitter.subscribe((people) => {
        if (people) {
          const profileName = this._sessionService.getProfilName();
          this.showChat = (profileName) ? people && ROLES.admin.indexOf(profileName) !== -1 : false;
          this.showChatPublic = people.profil.id == 4;
        } else {
          this.showChat = false;
          this.showChatPublic = false;
        }
      });

      this._appGlobalEventManagerService.spinnerEmitter.subscribe((spinner) => {
        if(spinner === false){
          this.spinner.hide()
          return;
        } else {
          this.spinner.show()
        }
      });

    });
  }

  ngAfterViewInit(): void {

    // this._sessionService.logguedAccountEmitter.subscribe((people) => {
    //   if (people) {
    //     if(this.showChat){
    //       console.log('SHOW')
    //       this.renderer2.removeClass(document.getElementById('jsd-widget'), 'd-none');
    //       this.renderer2.addClass(document.getElementById('jsd-widget'), 'd-block');
    //     }else{
    //       console.log('hide')
    //       this.renderer2.removeClass(document.getElementById('jsd-widget'), 'd-block');
    //       this.renderer2.addClass(document.getElementById('jsd-widget'), 'd-none');
    //     }
    //   } else {
    //     console.log('hide')
    //     this.renderer2.removeClass(document.getElementById('jsd-widget'), 'd-block');
    //     this.renderer2.addClass(document.getElementById('jsd-widget'), 'd-none');
    //   }
    // });

  }

  ngOnInit(): void {
    this.primeng.ripple.set(true);
    // Initialiser les traductions PrimeNG
    this.primeNGTranslationService.updateTranslations();

    // Écouter les changements de langue
    this.translocoService.langChanges$.subscribe((lang) => {
      localStorage.setItem('lang', lang)
      this.primeNGTranslationService.updateTranslations();
    });

  }

}
